import React, {useCallback} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {SettingsPanel} from '@pexip/media-components';
import {
    Button,
    FontVariant,
    Text,
    ThemeProvider,
    ToggleSwitch,
} from '@pexip/components';

import {SettingsActionsRow} from '../components/SettingsActionsRow';

import {BrowserCloseConfirmation} from '../types';
import {TestId} from '../../test/testIds';
import {useBrowserCloseConfirmationConfig} from '../hooks/useBrowserCloseConfirmationConfig';
import {useAmIApiParticipant} from '../hooks/useAmIApiParticipant';
import {useConfig} from '../config';
import {useConferenceStatus} from '../hooks/useConferenceStatus';
import {useLayoutOverlayEnabled} from '../hooks/meeting';
import {useMe} from '../hooks/useMe';
import {useInfinityContext} from '../hooks/useInfinityContext';
import {browserSupportsPtzConstraints} from '../services/Media.service';

export const AdvancedSettings: React.FC = () => {
    const infinity = useInfinityContext();
    const me = useMe(infinity.getMe);
    const meeting = infinity.getMeeting();

    const conferenceStatus = useConferenceStatus(
        meeting.getConferenceStatus,
        meeting.getRoomId,
    );

    const isDirectMedia = meeting.getConferenceFeatureFlags()?.isDirectMedia;
    const canControl = me?.canControl;
    const started = conferenceStatus?.started;
    const showMeetingSettings = !isDirectMedia && canControl;

    const isEnableOverlayText = useLayoutOverlayEnabled(
        meeting.getLayoutOverlayEnabled,
    );

    const {
        browserCloseConfirmation,
        setBrowserCloseConfirmation,
        shouldShowBrowserCloseConfirmation,
        isEnableBrowserCloseConfirmationByDefault,
    } = useBrowserCloseConfirmationConfig();
    const [
        showParticipantAuthenticatedState,
        setShowParticipantAuthenticatedState,
    ] = useConfig('showParticipantAuthenticatedState');
    const [preferPresInMix, setPreferPresInMix] = useConfig('preferPresInMix');
    const [fecc, setFecc] = useConfig('fecc');
    const amIApiParticipant = useAmIApiParticipant(infinity.getMe);

    const toggleBrowserClosePrevention = useCallback(() => {
        switch (browserCloseConfirmation) {
            case BrowserCloseConfirmation.Enabled:
                setBrowserCloseConfirmation(
                    BrowserCloseConfirmation.Disabled,
                    true,
                );
                break;
            case BrowserCloseConfirmation.Disabled:
                setBrowserCloseConfirmation(
                    BrowserCloseConfirmation.Enabled,
                    true,
                );
                break;
            case BrowserCloseConfirmation.Unset:
                if (isEnableBrowserCloseConfirmationByDefault) {
                    setBrowserCloseConfirmation(
                        BrowserCloseConfirmation.Disabled,
                        true,
                    );
                } else {
                    setBrowserCloseConfirmation(
                        BrowserCloseConfirmation.Enabled,
                        true,
                    );
                }
        }
    }, [
        browserCloseConfirmation,
        isEnableBrowserCloseConfirmationByDefault,
        setBrowserCloseConfirmation,
    ]);

    const {t} = useTranslation();

    return (
        <SettingsPanel>
            <ThemeProvider colorScheme="light">
                {showMeetingSettings && (
                    <>
                        <Text
                            fontVariant={FontVariant.BodyBold}
                            variant="tinted"
                        >
                            <Trans t={t} i18nKey="settings.meeting-settings">
                                Meeting settings
                            </Trans>
                        </Text>
                        <SettingsActionsRow
                            title={t(
                                'settings.show-name-labels',
                                'Show name labels',
                            )}
                            subtitle={t(
                                'settings.show-name-labels-desc',
                                'Displays participant names in the meeting layout for others to see. You may want to turn this off if taking a screenshot for social media',
                            )}
                        >
                            {/* TODO: The inline style for padding-left shound be removed when the settings panel is redesigned */}
                            <div style={{paddingInlineStart: '50px'}}>
                                <ToggleSwitch
                                    onChange={
                                        meeting.toggleLayoutOverlayTextEnabled
                                    }
                                    labelModifier="hidden"
                                    label={t(
                                        'settings.show-name-labels',
                                        'Show name labels',
                                    )}
                                    name="name-labels-overlay"
                                    checked={isEnableOverlayText}
                                />
                            </div>
                        </SettingsActionsRow>
                    </>
                )}
                <Text
                    fontVariant={FontVariant.BodyBold}
                    variant="tinted"
                    htmlTag="p"
                    className="mt-4 mb-0"
                >
                    <Trans t={t} i18nKey="settings.personal-settings">
                        Personal settings
                    </Trans>
                </Text>
                <SettingsActionsRow
                    title={t(
                        'settings.browser-close-prevention',
                        'Browser close confirmation',
                    )}
                    subtitle={t(
                        'settings.browser-close-prevention-desc',
                        'You must confirm before closing the browser or tab in which the meeting is currently running.',
                    )}
                    data-testid={TestId.ToggleBrowserCloseConformationRow}
                >
                    {/* TODO: The inline style for padding-left should be removed when the settings panel is redesigned */}
                    <div style={{paddingInlineStart: '50px'}}>
                        <ToggleSwitch
                            onChange={toggleBrowserClosePrevention}
                            labelModifier="hidden"
                            label={t(
                                'settings.browser-close-prevention',
                                'Browser close confirmation',
                            )}
                            name="name-labels-overlay"
                            data-testid={TestId.ToggleBrowserCloseConformation}
                            checked={shouldShowBrowserCloseConfirmation}
                        />
                    </div>
                </SettingsActionsRow>
                <SettingsActionsRow
                    title={t(
                        'settings.show-participant-authenticated-state-title',
                        'Show authenticated participants',
                    )}
                    subtitle={t(
                        'settings.show-participant-authenticated-state-desc',
                        'See indicators for participants who joined the meeting using SSO authentication.',
                    )}
                    data-testid={TestId.ToggleShowAuthenticatedParticipantsRow}
                >
                    <div style={{paddingInlineStart: '50px'}}>
                        <ToggleSwitch
                            onChange={isChecked =>
                                setShowParticipantAuthenticatedState(
                                    isChecked,
                                    true,
                                )
                            }
                            labelModifier="hidden"
                            label={t(
                                'settings.show-participant-authenticated-state-title',
                                'Show authenticated participants',
                            )}
                            name="name-labels-overlay"
                            data-testid={
                                TestId.ToggleShowAuthenticatedParticipants
                            }
                            checked={showParticipantAuthenticatedState}
                        />
                    </div>
                </SettingsActionsRow>
                {!amIApiParticipant && (
                    <SettingsActionsRow
                        title={t(
                            'settings.prefer-pres-in-mix',
                            'Prefer presentation in mix',
                        )}
                        subtitle={t(
                            'settings.prefer-pres-in-mix-desc',
                            'Enable this option to view shared presentation as part of the video mix whenever an Adaptive composition layout is being used.',
                        )}
                        data-testid={TestId.TogglePreferPresInMixRow}
                    >
                        <div style={{paddingInlineStart: '50px'}}>
                            <ToggleSwitch
                                onChange={isChecked =>
                                    setPreferPresInMix(isChecked, true)
                                }
                                labelModifier="hidden"
                                label={t(
                                    'settings.prefer-pres-in-mix',
                                    'Prefer presentation in mix',
                                )}
                                name="name-labels-overlay"
                                data-testid={TestId.TogglePreferPresInMix}
                                checked={preferPresInMix}
                            />
                        </div>
                    </SettingsActionsRow>
                )}
                {!amIApiParticipant && browserSupportsPtzConstraints() && (
                    <SettingsActionsRow
                        title={t('settings.fecc', 'Far-end camera control')}
                        subtitle={t(
                            'settings.fecc-desc',
                            'Enable this option to allow others to control your camera during a call.',
                        )}
                        data-testid={TestId.ToggleFeccRow}
                    >
                        <div style={{paddingInlineStart: '50px'}}>
                            <ToggleSwitch
                                onChange={isChecked => setFecc(isChecked, true)}
                                labelModifier="hidden"
                                label={t(
                                    'settings.fecc',
                                    'Far-end camera control',
                                )}
                                name="name-labels-overlay"
                                data-testid={TestId.ToggleFecc}
                                checked={fecc}
                            />
                        </div>
                    </SettingsActionsRow>
                )}
                {showMeetingSettings && !started && (
                    <SettingsActionsRow
                        title={t('settings.start-meeting', 'Start meeting')}
                        subtitle={t(
                            'settings.start-meeting-desc',
                            'Starts the meeting',
                        )}
                    >
                        <Button
                            onClick={meeting.startConference}
                            variant="tertiary"
                            data-testid={TestId.ButtonStartConference}
                        >
                            {t('settings.start-meeting', 'Start meeting')}
                        </Button>
                    </SettingsActionsRow>
                )}
            </ThemeProvider>
        </SettingsPanel>
    );
};
